import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import ConversationList from '../components/messaging/ConversationList';
import ConversationThread from '../components/messaging/ConversationThread';
import MessageComposer from '../components/messaging/MessageComposer';
import PendingSenderView from '../components/messaging/PendingSenderView';
import { RequestDocumentsModal, SendDocumentsModal } from '../components/messaging/DocumentModals';
import { isHardStop, templateContext, nextAppointment } from '../components/messaging/threadUtils';

function Messaging({ practiceId }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingDisambiguations, setPendingDisambiguations] = useState([]);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [contentFilter, setContentFilter] = useState('all'); // all, unread, ai
  const [error, setError] = useState(null);
  const [consentStatus, setConsentStatus] = useState(null);
  // Batch 2 (Oct 2026): patient details, handoff summary, unidentified senders
  const [patientDetail, setPatientDetail] = useState(null);
  const [convSummary, setConvSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [selectedPending, setSelectedPending] = useState(null);
  const lastDocEventRef = useRef(null);
  const [sendTimeHint, setSendTimeHint] = useState(null);
  const [patientSentiment, setPatientSentiment] = useState(null);
  const [personalizing, setPersonalizing] = useState(false);
  const [personalizedDraft, setPersonalizedDraft] = useState(null);
  const [practiceInfo, setPracticeInfo] = useState(null); // name + phone for template fields
  // Context rail data for the selected patient (Oct 2026 thread redesign).
  const [patientAppointments, setPatientAppointments] = useState([]);
  const [patientConsents, setPatientConsents] = useState([]);
  const [aiConfig, setAiConfig] = useState(null);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const composerRef = useRef(null);
  // Secure document link (item 2 of the Oct 2026 SMS enhancements)
  const [patientDocuments, setPatientDocuments] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [docModal, setDocModal] = useState(null); // 'request' | 'send' | null
  const [busyDocumentId, setBusyDocumentId] = useState(null);
  // GH-messaging-grouping: Ohmd-style inbox groups.
  // activeGroup is a bucket key ('assigned_to_me' | 'starred_by_me' |
  // 'unassigned' | 'website_chats' | 'category:<name>' | 'all').
  const [activeGroup, setActiveGroup] = useState('all');
  const [inboxGroups, setInboxGroups] = useState({
    total: 0, assigned_to_me: 0, starred_by_me: 0, unassigned: 0, website_chats: 0, by_category: {},
  });

  const loadInboxGroups = useCallback(async () => {
    if (!practiceId) return;
    try {
      const res = await api.getSmsInboxGroups(practiceId);
      if (res?.success) setInboxGroups(res.data);
    } catch { /* non-blocking */ }
  }, [practiceId]);

  // commons #205: filter chips fire loadConversations on every change; when two
  // requests overlap (e.g. "Assigned to me" then straight back to "All"), the
  // slower one used to land last and overwrite the list with the wrong bucket
  // — the list then looked empty or unfiltered. Only the latest request wins.
  const loadSeq = useRef(0);
  // Load conversations
  const loadConversations = useCallback(async () => {
    if (!practiceId) return;
    const seq = ++loadSeq.current;
    try {
      setLoading(true);
      const response = await api.getSmsConversations(practiceId, {
        limit: 50,
        search: searchQuery || undefined,
        // GH-194: api.getSmsConversations reads options.hasUnread (camelCase),
        // not options.has_unread. Passing snake_case here silently dropped the
        // filter — the Unread view was returning everything.
        hasUnread: contentFilter === 'unread' ? 'true' : undefined,
        group: activeGroup !== 'all' ? activeGroup : undefined,
      });
      if (seq !== loadSeq.current) return; // a newer filter request superseded this one
      let filtered = response.data || [];
      // Client-side filter for AI conversations (has ai_enabled or system messages)
      if (contentFilter === 'ai') {
        filtered = filtered.filter(c => c.ai_enabled || c.ai_takeover_status);
      }
      setConversations(filtered);
      setUnreadCount(response.unread_count || 0);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      console.error('Error loading conversations:', err);
      setError('Failed to load conversations');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [practiceId, searchQuery, contentFilter, activeGroup]);

  useEffect(() => { loadInboxGroups(); }, [loadInboxGroups, activeGroup, conversations.length]);

  // Load pending disambiguations
  const loadPendingDisambiguations = useCallback(async () => {
    if (!practiceId) return;
    try {
      const response = await api.getSmsPendingDisambiguations(practiceId);
      setPendingDisambiguations(response.data || []);
    } catch (err) {
      console.error('Error loading pending disambiguations:', err);
    }
  }, [practiceId]);

  // Load messages for selected conversation
  const loadMessages = useCallback(async (conversationId, { silent = false } = {}) => {
    try {
      // Only show the loading spinner on user-initiated loads. Background
      // polls (every 10s) would otherwise flash the spinner over the
      // existing messages every tick, making the thread unreadable.
      if (!silent) setMessagesLoading(true);
      const response = await api.getSmsConversation(conversationId, {
        messageLimit: 100
      });
      setMessages(response.data?.messages || []);

      // A document arrived since the last load: refresh the rail's documents
      // card (it is loaded once per selection, not on every poll).
      const docEvents = (response.data?.messages || []).filter(m => m.event_type && String(m.event_type).startsWith('document_'));
      const newestDocEvent = docEvents.length ? String(docEvents[docEvents.length - 1].id) : null;
      if (newestDocEvent && newestDocEvent !== lastDocEventRef.current && response.data?.patient_id) {
        lastDocEventRef.current = newestDocEvent;
        api.getPatientDocuments(practiceId, response.data.patient_id)
          .then(r => {
            setPatientDocuments(Array.isArray(r?.data?.documents) ? r.data.documents : []);
            if (Array.isArray(r?.data?.categories)) setDocumentCategories(r.data.categories);
          })
          .catch(() => {});
      }

      // Update selected conversation with full data
      if (response.data) {
        setSelectedConversation(prev => ({
          ...prev,
          ...response.data,
          messages: undefined
        }));

        // Silent (background-poll) loads skip everything below — consent /
        // send-time / sentiment / mark-read don't change between 10s ticks.
        // Re-fetching all of them every poll burned ~6 API calls per
        // selected conversation per minute for no benefit.
        if (silent) return;

        // Load consent status for this patient
        try {
          const consentResponse = await api.getPatientSmsConsent(practiceId, response.data.patient_id);
          setConsentStatus(consentResponse.data || { consent_status: 'none', consent_given: 0 });
        } catch (err) {
          console.error('Error loading consent status:', err);
          setConsentStatus({ consent_status: 'none', consent_given: 0 });
        }

        // Context rail: upcoming visit + per-channel consent. Non-blocking; the
        // thread renders without them and the rail simply omits the card.
        if (response.data.patient_id) {
          const pid = response.data.patient_id;
          api.getPatientAppointments(practiceId, pid)
            .then(r => setPatientAppointments(Array.isArray(r?.data) ? r.data : []))
            .catch(() => setPatientAppointments([]));
          api.getPatientConsents(practiceId, pid)
            .then(r => setPatientConsents(Array.isArray(r?.data?.consents) ? r.data.consents : []))
            .catch(() => setPatientConsents([]));
          api.getPatientDocuments(practiceId, pid)
            .then(r => {
              setPatientDocuments(Array.isArray(r?.data?.documents) ? r.data.documents : []);
              if (Array.isArray(r?.data?.categories)) setDocumentCategories(r.data.categories);
            })
            .catch(() => setPatientDocuments([]));
          api.getPatientDetail(practiceId, pid)
            .then(r => setPatientDetail(r?.data || null))
            .catch(() => setPatientDetail(null));
          setSummaryLoading(true);
          api.getSmsConversationSummary(practiceId, conversationId)
            .then(r => setConvSummary(r?.data || null))
            .catch(() => setConvSummary(null))
            .finally(() => setSummaryLoading(false));
        } else {
          setPatientAppointments([]);
          setPatientConsents([]);
          setPatientDocuments([]);
          setPatientDetail(null);
          setConvSummary(null);
        }
      }

      // Load AI data for this patient
      if (response.data?.patient_id) {
        const pid = response.data.patient_id;
        api.getPatientSendTime(practiceId, pid).then(r => {
          if (r.success && r.data) setSendTimeHint(r.data);
          else setSendTimeHint(null);
        }).catch(() => setSendTimeHint(null));

        api.getPatientSentiment(practiceId, pid).then(r => {
          if (r.success && r.data) setPatientSentiment(r.data);
          else setPatientSentiment(null);
        }).catch(() => setPatientSentiment(null));
      }

      // Mark as read — non-fatal; don't surface a "failed to load" banner
      // if the messages themselves loaded successfully.
      try {
        await api.markSmsConversationRead(conversationId);
        loadConversations();
      } catch (err) {
        console.error('Error marking conversation as read:', err);
      }
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('Failed to load messages');
    } finally {
      setMessagesLoading(false);
    }
  }, [loadConversations, practiceId]);

  // Initial load
  useEffect(() => {
    loadConversations();
    loadPendingDisambiguations();
  }, [loadConversations, loadPendingDisambiguations]);

  // Practice-level composer data: the AI's booking link and the SMS templates.
  useEffect(() => {
    if (!practiceId) return;
    let cancelled = false;
    api.getAiConfig(practiceId)
      .then(r => { if (!cancelled) setAiConfig(r?.data || null); })
      .catch(() => { if (!cancelled) setAiConfig(null); });
    api.getPractice(practiceId)
      .then(r => { if (!cancelled) setPracticeInfo(r?.data || null); })
      .catch(() => { if (!cancelled) setPracticeInfo(null); });
    api.getTemplates(practiceId, null, 'sms')
      .then(r => {
        if (cancelled) return;
        const list = Array.isArray(r?.data) ? r.data : Array.isArray(r?.data?.templates) ? r.data.templates : [];
        setSmsTemplates(list.filter(t => t && t.content && (t.is_active == null || Number(t.is_active) === 1)));
      })
      .catch(() => { if (!cancelled) setSmsTemplates([]); });
    return () => { cancelled = true; };
  }, [practiceId]);

  const bookingLink = aiConfig?.booking_link_template
    ? String(aiConfig.booking_link_template).replace('{practice_id}', String(practiceId))
    : null;
  const lastOutbound = [...messages].reverse().find(m => m.direction === 'outbound' && m.from_phone);
  const senderPhone = lastOutbound?.from_phone || null;
  // Hard block: a STOP on file means the carrier rejects anything we send.
  const smsBlocked = isHardStop(patientConsents, consentStatus);

  // ---- secure document link handlers ----
  const reloadDocuments = useCallback(async () => {
    if (!selectedConversation?.patient_id) return;
    try {
      const r = await api.getPatientDocuments(practiceId, selectedConversation.patient_id);
      setPatientDocuments(Array.isArray(r?.data?.documents) ? r.data.documents : []);
      if (Array.isArray(r?.data?.categories)) setDocumentCategories(r.data.categories);
    } catch (err) {
      console.error('Error loading documents:', err);
    }
  }, [practiceId, selectedConversation?.patient_id]);

  const handleOpenDocument = async (doc) => {
    try {
      const r = await api.getPatientDocumentUrl(practiceId, doc.id);
      if (r?.data?.url) window.open(r.data.url, '_blank', 'noopener');
    } catch (err) {
      alert(`Could not open the document: ${err.message}`);
    }
  };

  const handleRecategorize = async (doc, category) => {
    setBusyDocumentId(doc.id);
    try {
      await api.recategorizePatientDocument(practiceId, doc.id, category);
      await reloadDocuments();
    } catch (err) {
      alert(`Could not change the document type: ${err.message}`);
    } finally {
      setBusyDocumentId(null);
    }
  };

  const handlePushDocument = async (doc) => {
    const label = doc.label || doc.category;
    if (!window.confirm(`File "${label}" to this patient's athena chart?`)) return;
    setBusyDocumentId(doc.id);
    try {
      await api.pushPatientDocument(practiceId, doc.id);
      await reloadDocuments();
      if (selectedConversation) loadMessages(selectedConversation.id, { silent: true });
    } catch (err) {
      alert(`Push failed: ${err.message}`);
      await reloadDocuments();
    } finally {
      setBusyDocumentId(null);
    }
  };

  const handleDocumentsSent = async () => {
    await reloadDocuments();
    if (selectedConversation) await loadMessages(selectedConversation.id, { silent: true });
  };

  // Load messages when conversation is selected
  const selectedConversationId = selectedConversation?.id;
  useEffect(() => {
    if (selectedConversationId) {
      loadMessages(selectedConversationId);
    }
  }, [selectedConversationId, loadMessages]);

  // Polling for new messages. Sidebar + disambig poll at 30s; the
  // actively-selected conversation polls at 10s so inbound replies render
  // closer to real-time without forcing a manual refresh.
  //
  // CRITICAL: depend on `selectedConversation?.id` (a stable scalar) NOT
  // on `selectedConversation` (the full object). `loadMessages` does
  // setSelectedConversation(prev => ({ ...prev, ...response.data })) on
  // every poll — if we depended on the object itself, every successful
  // poll would mutate the reference, tear down + re-create the interval,
  // and the timer would never fire. That's the actual cause of the
  // "have to refresh to see new messages" report.
  // (selectedConversationId is already declared above for the load-on-select effect.)
  useEffect(() => {
    const slowInterval = setInterval(() => {
      loadConversations();
      loadPendingDisambiguations();
    }, 30000);
    let fastInterval = null;
    if (selectedConversationId) {
      fastInterval = setInterval(() => {
        loadMessages(selectedConversationId, { silent: true });
      }, 10000);
    }
    return () => {
      clearInterval(slowInterval);
      if (fastInterval) clearInterval(fastInterval);
    };
  }, [loadConversations, loadPendingDisambiguations, selectedConversationId, loadMessages]);

  // Handle personalizing a message draft
  const handlePersonalize = async (draftContent) => {
    if (!selectedConversation?.patient_id || !draftContent.trim() || personalizing) return;
    try {
      setPersonalizing(true);
      const result = await api.personalizeMessage(practiceId, {
        patientId: selectedConversation.patient_id,
        templateContent: draftContent.trim(),
      });
      if (result.success && result.data?.personalizedContent) {
        setPersonalizedDraft(result.data.personalizedContent);
      }
    } catch (err) {
      console.error('Error personalizing message:', err);
    } finally {
      setPersonalizing(false);
    }
  };

  // Handle sending a message. No consent on file: ask once, then send with a
  // logged override. STOP on file: the server refuses and we say why.
  const handleSendMessage = async (content, { overrideConsent = false } = {}) => {
    if (!selectedConversation || !content.trim()) return;

    try {
      await api.sendSmsMessage({
        conversation_id: selectedConversation.id,
        content: content.trim(),
        practice_id: practiceId,
        ...(overrideConsent ? { override_consent: true } : {}),
      });

      await loadMessages(selectedConversation.id);
    } catch (err) {
      if (err.code === 'NO_SMS_CONSENT' && !overrideConsent) {
        const go = window.confirm('No texting consent is on file for this patient.\n\nOK sends anyway (this is logged). Cancel to send a consent request from the rail first.');
        if (go) return handleSendMessage(content, { overrideConsent: true });
        return;
      }
      console.error('Error sending message:', err);
      alert(`Failed to send message: ${err.message}`);
      throw err;
    }
  };

  // Start a new conversation. Returns normally on success; throws with
  // err.code NO_SMS_CONSENT / SMS_OPTED_OUT so the dialog can show the gate.
  const handleStartConversation = async (patientId, content, { overrideConsent = false } = {}) => {
    await api.startSmsConversation({
      patient_id: patientId,
      content: content.trim(),
      practice_id: practiceId,
      ...(overrideConsent ? { override_consent: true } : {}),
    });
    setShowNewConversation(false);
    await loadConversations();
  };

  const handleRefreshSummary = async () => {
    if (!selectedConversation) return;
    setSummaryLoading(true);
    try {
      const r = await api.getSmsConversationSummary(practiceId, selectedConversation.id, true);
      setConvSummary(r?.data || null);
    } catch (err) {
      console.error('Summary refresh failed:', err);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleResendNotice = async () => {
    if (!selectedConversation) return;
    try {
      await api.resendSmsPhiNotice(practiceId, selectedConversation.id);
      await loadMessages(selectedConversation.id, { silent: true });
    } catch (err) {
      alert(`Could not send the notice: ${err.message}`);
    }
  };

  const handleSelectPending = (p) => {
    setSelectedPending(p);
    setSelectedConversation(null);
  };

  // Handle resolving a sub-conversation
  const handleResolve = async (subConversationId, notes) => {
    try {
      await api.resolveSmsSubConversation(subConversationId, notes);
      if (selectedConversation) {
        await loadMessages(selectedConversation.id);
      }
    } catch (err) {
      console.error('Error resolving conversation:', err);
      alert(`Failed to resolve: ${err.message}`);
    }
  };

  // Handle granting SMS consent
  // The rail's four channel pills come from the typed consents, the header pill from the
  // legacy row. Reloading only one left the rail stale until a page refresh (commons #399).
  const reloadConsent = async (patientId) => {
    const [legacy, typed] = await Promise.all([
      api.getPatientSmsConsent(practiceId, patientId).catch(() => null),
      api.getPatientConsents(practiceId, patientId).catch(() => null),
    ]);
    setConsentStatus(legacy?.data || { consent_status: 'none', consent_given: 0 });
    setPatientConsents(Array.isArray(typed?.data?.consents) ? typed.data.consents : []);
  };

  const handleGrantConsent = async (patientId) => {
    try {
      await api.grantSmsConsent(practiceId, patientId, 'verbal');
      await reloadConsent(patientId);
    } catch (err) {
      console.error('Error granting consent:', err);
      alert(`Failed to grant consent: ${err.message}`);
    }
  };

  // Handle revoking SMS consent
  const handleRevokeConsent = async (patientId) => {
    if (!window.confirm('Are you sure you want to revoke SMS consent for this patient?')) return;
    try {
      await api.withdrawSmsConsent(practiceId, patientId, 'Manual revoke by staff');
      await reloadConsent(patientId);
    } catch (err) {
      console.error('Error revoking consent:', err);
      alert(`Failed to revoke consent: ${err.message}`);
    }
  };

  // Handle assigning a pending message to a patient
  const handleAssignPending = async (pendingId, patientId) => {
    await api.assignSmsPendingToPatient(pendingId, patientId);
    setSelectedPending(null);
    await loadPendingDisambiguations();
    await loadConversations();
    // Open the patient's thread, which now holds the text.
    try {
      const r = await api.getSmsConversations(practiceId, { limit: 50 });
      const conv = (r?.data || []).find(c => String(c.patient_id) === String(patientId));
      if (conv) setSelectedConversation(conv);
    } catch (err) {
      console.error('Could not open the assigned conversation:', err);
    }
  };

  const handleDismissPending = async () => {
    setSelectedPending(null);
    await loadPendingDisambiguations();
  };

  if (!practiceId) {
    return (
      <div style={styles.noPractice}>
        <div style={styles.noPracticeCard}>
          <AlertIcon />
          <h3 style={styles.noPracticeTitle}>No Practice Selected</h3>
          <p style={styles.noPracticeText}>
            Please select a practice to view messages.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>

      {/* Main Messaging Interface */}
      <div style={styles.messagingContainer}>
        {/* Left Sidebar - Conversation List */}
        <div style={styles.sidebar}>
          {/* Header */}
          <div style={styles.sidebarHeader}>
            <div style={styles.headerTop}>
              <div>
                <h1 style={styles.title}>Messages</h1>
                <p style={styles.subtitle}>
                  {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                </p>
              </div>
              <button
                style={styles.newButton}
                onClick={() => setShowNewConversation(true)}
              >
                <PlusIcon />
                New
              </button>
            </div>

            {/* Search */}
            <div style={styles.searchWrapper}>
              <SearchIcon />
              <input
                type="text"
                placeholder="Search patients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.searchInput}
              />
              {searchQuery && (
                <button
                  style={styles.clearSearch}
                  onClick={() => setSearchQuery('')}
                >
                  <CloseIcon />
                </button>
              )}
            </div>

            {/* Content Filter Tabs */}
            <div style={styles.filterTabs}>
              {[
                { key: 'all', label: 'All' },
                { key: 'unread', label: 'Unread' },
                { key: 'ai', label: 'AI' },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setContentFilter(f.key)}
                  style={{
                    ...styles.filterTab,
                    ...(contentFilter === f.key ? styles.filterTabActive : {})
                  }}
                >
                  {f.label}
                  {f.key === 'unread' && unreadCount > 0 && (
                    <span style={styles.filterBadge}>{unreadCount}</span>
                  )}
                </button>
              ))}
            </div>

            {/* GH-messaging-grouping: Ohmd-style inbox groups. Sits underneath
                the search + filter row so the linear list becomes a bucket
                view: Assigned to me / Starred / Unassigned / Website chats /
                by-category. Clicking a bucket refetches with ?group=<key>. */}
            <div style={styles.groupRail}>
              {[
                { key: 'all',            label: 'All',            count: inboxGroups.total },
                { key: 'assigned_to_me', label: 'Assigned to me', count: inboxGroups.assigned_to_me },
                { key: 'starred_by_me',  label: '★ Starred', count: inboxGroups.starred_by_me },
                { key: 'unassigned',     label: 'Unassigned',     count: inboxGroups.unassigned },
                { key: 'website_chats',  label: 'Website chats',  count: inboxGroups.website_chats },
                { key: 'complaints',     label: '⚠ Complaints',   count: inboxGroups.complaints },
              ].map(g => (
                <button
                  key={g.key}
                  onClick={() => setActiveGroup(g.key)}
                  style={{ ...styles.groupChip, ...(activeGroup === g.key ? styles.groupChipActive : {}) }}
                  title={g.label}
                >
                  {g.label}
                  {g.count > 0 && <span style={styles.groupCount}>{g.count}</span>}
                </button>
              ))}
              {Object.entries(inboxGroups.by_category || {}).map(([cat, n]) => {
                const key = `category:${cat}`;
                return (
                  <button
                    key={key}
                    onClick={() => setActiveGroup(key)}
                    style={{ ...styles.groupChip, ...(activeGroup === key ? styles.groupChipActive : {}) }}
                    title={cat}
                  >
                    {cat}
                    <span style={styles.groupCount}>{n}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Conversation List */}
          <ConversationList
            conversations={conversations}
            selectedId={selectedConversation?.id}
            onSelect={(c) => { setSelectedPending(null); setSelectedConversation(c); }}
            loading={loading}
            pending={pendingDisambiguations}
            selectedPendingId={selectedPending?.id || null}
            onSelectPending={handleSelectPending}
            onStarToggle={async (conv) => {
              // Optimistic flip so the icon reacts instantly
              setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, starred: !c.starred } : c));
              try {
                if (conv.starred) await api.unstarSmsConversation(conv.id, practiceId);
                else              await api.starSmsConversation(conv.id, practiceId);
                loadInboxGroups();
              } catch (err) {
                // Rollback on failure
                setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, starred: conv.starred } : c));
                console.error('Star toggle failed:', err.message);
              }
            }}
          />
        </div>

        {/* Right Side - Message Thread */}
        <div style={styles.mainContent}>
          {error && (
            <div style={styles.errorBanner}>
              <AlertIcon />
              <span>{error}</span>
              <button onClick={() => setError(null)} style={styles.errorClose}>
                <CloseIcon />
              </button>
            </div>
          )}

          {selectedPending ? (
            <PendingSenderView
              practiceId={practiceId}
              pending={selectedPending}
              onAssigned={handleAssignPending}
              onDismissed={handleDismissPending}
              onAsked={loadPendingDisambiguations}
            />
          ) : selectedConversation ? (
            <>
              <ConversationThread
                conversation={selectedConversation}
                messages={messages}
                loading={messagesLoading}
                onResolve={handleResolve}
                consentStatus={consentStatus}
                onGrantConsent={handleGrantConsent}
                onRevokeConsent={handleRevokeConsent}
                practiceId={practiceId}
                patient={patientDetail}
                summary={convSummary}
                summaryLoading={summaryLoading}
                onRefreshSummary={handleRefreshSummary}
                appointments={patientAppointments}
                consents={patientConsents}
                insights={{
                  sendTime: sendTimeHint,
                  sendTimeLabel: sendTimeHint && sendTimeHint.best_hour_utc != null ? formatSendTimeHint(sendTimeHint) : null,
                  sentiment: patientSentiment,
                }}
                onBookingLink={bookingLink ? () => composerRef.current?.insert(bookingLink) : null}
                documents={patientDocuments}
                documentCategories={documentCategories}
                busyDocumentId={busyDocumentId}
                onRequestDocument={selectedConversation.patient_id ? () => setDocModal('request') : null}
                onSendDocuments={selectedConversation.patient_id ? () => setDocModal('send') : null}
                onOpenDocument={handleOpenDocument}
                onRecategorize={handleRecategorize}
                onPushDocument={handlePushDocument}
                onAiStatusChange={() => {
                  // Reload both the sidebar (status badges) AND the
                  // selected conversation (so AiTakeoverControls' local
                  // subConversation.ai_takeover_status reflects the new
                  // state — without this the button click looked like a
                  // no-op even though the backend had flipped the status).
                  loadConversations();
                  if (selectedConversation) loadMessages(selectedConversation.id);
                }}
              >
                {/* Personalized Draft Preview */}
                {personalizedDraft && (
                  <div style={msgAiStyles.draftPreview}>
                    <div style={msgAiStyles.draftHeader}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#667eea' }}>&#9733; AI Personalized</span>
                      <button onClick={() => setPersonalizedDraft(null)} style={msgAiStyles.draftClose}>Dismiss</button>
                    </div>
                    <div style={msgAiStyles.draftText}>{personalizedDraft}</div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button
                        onClick={() => { handleSendMessage(personalizedDraft); setPersonalizedDraft(null); }}
                        style={msgAiStyles.draftAccept}
                      >Send This</button>
                      <button
                        onClick={() => { composerRef.current?.replace(personalizedDraft); setPersonalizedDraft(null); }}
                        style={msgAiStyles.draftReject}
                        title="Put the draft in the reply box so you can change it before sending"
                      >Edit first</button>
                      <button onClick={() => setPersonalizedDraft(null)} style={msgAiStyles.draftReject}>
                        Discard
                      </button>
                    </div>
                  </div>
                )}
                <MessageComposer
                  ref={composerRef}
                  onSend={handleSendMessage}
                  onPersonalize={handlePersonalize}
                  personalizing={personalizing}
                  templates={smsTemplates}
                  templateContext={templateContext({ patient: patientDetail, conversation: selectedConversation, practice: practiceInfo, appointment: nextAppointment(patientAppointments), bookingLink })}
                  bookingLink={bookingLink}
                  senderPhone={senderPhone}
                  patientFirstName={selectedConversation.first_name}
                  blocked={smsBlocked}
                  sendBlockedReason={smsBlocked ? 'this patient opted out of texts (STOP)' : null}
                  onResendNotice={handleResendNotice}
                  onRequestDocument={selectedConversation.patient_id && !smsBlocked ? () => setDocModal('request') : null}
                  onSendDocuments={selectedConversation.patient_id && !smsBlocked ? () => setDocModal('send') : null}
                />
              </ConversationThread>
            </>
          ) : (
            <div style={styles.emptyState}>
              <MessageSquareIcon />
              <h3 style={styles.emptyTitle}>Select a conversation</h3>
              <p style={styles.emptyText}>
                Choose a conversation from the list to view messages
              </p>
            </div>
          )}
        </div>
      </div>

      {docModal === 'request' && selectedConversation && (
        <RequestDocumentsModal practiceId={practiceId} conversation={selectedConversation} categories={documentCategories}
          onClose={() => setDocModal(null)} onSent={handleDocumentsSent} />
      )}
      {docModal === 'send' && selectedConversation && (
        <SendDocumentsModal practiceId={practiceId} conversation={selectedConversation}
          onClose={() => setDocModal(null)} onSent={handleDocumentsSent} />
      )}

      {/* New Conversation Modal */}
      {showNewConversation && (
        <NewConversationModal
          practiceId={practiceId}
          onStart={handleStartConversation}
          onClose={() => setShowNewConversation(false)}
        />
      )}
    </div>
  );
}

// New Conversation Modal Component
function NewConversationModal({ practiceId, onStart, onClose }) {
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadPatients = async () => {
      if (!searchTerm || searchTerm.length < 2) {
        setPatients([]);
        return;
      }

      try {
        setLoading(true);
        // Was fetching only the first 20 patients (limit=20, offset=0) and
        // filtering client-side — any patient beyond patient #20 by sort
        // order was invisible to the search. Now passes `search` to the
        // backend so the DB does an ILIKE across first_name / last_name /
        // email / phone / mobile_phone / dob / city / zip across the
        // ENTIRE patient table for this practice.
        const response = await api.getPatients(practiceId, {
          limit: 50,
          offset: 0,
          search: searchTerm,
        });
        setPatients(response.data || []);
      } catch (err) {
        console.error('Error loading patients:', err);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(loadPatients, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm, practiceId]);

  const [sending, setSending] = useState(false);
  const [gate, setGate] = useState(null);   // 'no_consent' | 'opted_out' | null
  const [err, setErr] = useState(null);
  const [consentSent, setConsentSent] = useState(false);

  const submit = async (overrideConsent = false) => {
    if (!selectedPatient || !message.trim() || sending) return;
    setSending(true); setErr(null);
    try {
      await onStart(selectedPatient.id, message, { overrideConsent });
    } catch (e) {
      if (e.code === 'NO_SMS_CONSENT') setGate('no_consent');
      else if (e.code === 'SMS_OPTED_OUT') setGate('opted_out');
      else setErr(e.message || 'Could not send');
    } finally {
      setSending(false);
    }
  };
  const sendConsentRequest = async () => {
    setSending(true); setErr(null);
    try {
      await api.sendSmsConsentRequest(practiceId, selectedPatient.id);
      setConsentSent(true);
    } catch (e) {
      setErr(e.message || 'Could not send the consent request');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="nh-thread" style={{ position: 'fixed', inset: 0, background: 'transparent', display: 'block', zIndex: 30 }}>
      <div className="nh-modal-bg" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="nh-modal nh-docmodal nh-newconv" role="dialog" aria-modal="true">
          <h4>New conversation</h4>
          <form onSubmit={e => { e.preventDefault(); submit(false); }}>
            {!selectedPatient && (
              <>
                <input className="search" type="text" placeholder="Search patients by name, phone or date of birth…" value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setGate(null); }} autoFocus />
                {loading && <p className="hint">Searching…</p>}
                {patients.length > 0 && (
                  <div className="plist">
                    {patients.map(patient => (
                      <div key={patient.id} className="prow" onClick={() => { setSelectedPatient(patient); setGate(null); setConsentSent(false); }}>
                        <div className="pav">{getInitials(patient.first_name, patient.last_name)}</div>
                        <div style={{ minWidth: 0 }}>
                          <b>{patient.first_name} {patient.last_name}</b>
                          <small>{[patient.phone, patient.date_of_birth ? `DOB ${String(patient.date_of_birth).slice(0, 10)}` : null].filter(Boolean).join(' · ')}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {!loading && searchTerm.length >= 2 && patients.length === 0 && <p className="hint">No patients match.</p>}
              </>
            )}

            {selectedPatient && (
              <div className="prow sel" style={{ marginBottom: 10, cursor: 'default' }}>
                <div className="pav">{getInitials(selectedPatient.first_name, selectedPatient.last_name)}</div>
                <div style={{ minWidth: 0 }}>
                  <b>{selectedPatient.first_name} {selectedPatient.last_name}</b>
                  <small>{selectedPatient.phone}</small>
                </div>
                <button type="button" className="chg" onClick={() => { setSelectedPatient(null); setGate(null); setConsentSent(false); }}>Change</button>
              </div>
            )}

            <label className="lbl">Message
              <textarea placeholder="Type your message…" value={message} onChange={(e) => setMessage(e.target.value)} rows={4} />
            </label>

            {gate === 'no_consent' && (
              <div className="nh-gate">
                <span><b>No texting consent is on file for this patient.</b> You can ask for it with one tap, or send anyway (the override is logged).</span>
                {consentSent && <span>✓ Consent request sent. The patient replies YES to opt in.</span>}
                <div className="row">
                  <button type="button" className="btn" onClick={sendConsentRequest} disabled={sending || consentSent}>{consentSent ? 'Request sent' : 'Send consent request'}</button>
                  <button type="button" className="btn primary" onClick={() => submit(true)} disabled={sending}>Send anyway</button>
                </div>
              </div>
            )}
            {gate === 'opted_out' && (
              <div className="nh-gate" style={{ background: '#FFE4E9', borderColor: '#FDB5C3', color: '#9F1239' }}>
                <span><b>This patient texted STOP.</b> The carrier blocks texts to them until they text START. Call them instead.</span>
              </div>
            )}
            {err && <div className="err">{err}</div>}

            <div className="row">
              <button type="button" className="btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn primary" disabled={!selectedPatient || !message.trim() || sending || gate === 'opted_out'}>
                {sending ? 'Sending…' : 'Send message'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// AI helper functions
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function formatSendTimeHint(data) {
  const hour = data.best_hour_utc ?? 0;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h = hour % 12 || 12;
  const day = data.best_day_of_week != null ? DAYS[data.best_day_of_week] + ' ' : '';
  return `${day}${h} ${ampm}`;
}
// Utility functions
function getInitials(firstName, lastName) {
  const first = firstName ? firstName.charAt(0).toUpperCase() : '';
  const last = lastName ? lastName.charAt(0).toUpperCase() : '';
  return first + last || '?';
}

// Icon Components
function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}


function MessageSquareIcon() {
  return (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}



const styles = {
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 120px)',
    backgroundColor: '#f3f4f6',
    margin: '-20px -24px',
    width: 'calc(100% + 48px)'
  },
  noPractice: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 'calc(100vh - 120px)',
    backgroundColor: '#f3f4f6',
    margin: '-20px -24px',
    width: 'calc(100% + 48px)'
  },
  noPracticeCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    color: '#6b7280'
  },
  noPracticeTitle: {
    margin: '16px 0 8px 0',
    color: '#374151',
    fontSize: '18px'
  },
  noPracticeText: {
    margin: 0,
    color: '#6b7280',
    fontSize: '14px'
  },
  alertBanner: {
    backgroundColor: '#fef3c7',
    borderBottom: '1px solid #fcd34d',
    padding: '12px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px',
    flexShrink: 0
  },
  alertContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: '#92400e'
  },
  alertCount: {
    marginLeft: '8px',
    fontSize: '13px',
    opacity: 0.8
  },
  messagingContainer: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    backgroundColor: '#fff'
  },
  sidebar: {
    width: '360px',
    minWidth: '360px',
    borderRight: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff'
  },
  sidebarHeader: {
    padding: '20px',
    borderBottom: '1px solid #f3f4f6'
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827'
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '13px',
    color: '#6b7280'
  },
  newButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease'
  },
  searchWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    color: '#9ca3af'
  },
  searchInput: {
    width: '100%',
    padding: '10px 12px 10px 40px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: '#f9fafb',
    outline: 'none',
    transition: 'all 0.15s ease'
  },
  clearSearch: {
    position: 'absolute',
    right: '8px',
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    color: '#9ca3af',
    display: 'flex'
  },
  filterTabs: {
    display: 'flex',
    gap: '4px',
    marginTop: '12px'
  },
  groupRail: {
    display: 'flex',
    gap: '6px',
    marginTop: '10px',
    flexWrap: 'wrap',
  },
  groupChip: {
    padding: '4px 10px',
    border: '1px solid #e5e7eb',
    borderRadius: '999px',
    background: '#fff',
    color: '#374151',
    fontSize: '11px',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
  },
  groupChipActive: {
    background: '#0f172a',
    color: '#fff',
    borderColor: '#0f172a',
  },
  groupCount: {
    background: 'rgba(0,0,0,0.08)',
    color: 'inherit',
    padding: '0px 6px',
    borderRadius: '999px',
    fontSize: '10px',
    fontWeight: 600,
  },
  filterTab: {
    flex: 1,
    padding: '6px 8px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    background: '#f9fafb',
    color: '#6b7280',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    transition: 'all 0.15s ease'
  },
  filterTabActive: {
    background: '#eff6ff',
    borderColor: '#3b82f6',
    color: '#1d4ed8'
  },
  filterBadge: {
    fontSize: '10px',
    fontWeight: '600',
    backgroundColor: '#3b82f6',
    color: '#fff',
    padding: '1px 6px',
    borderRadius: '10px',
    minWidth: '16px',
    textAlign: 'center'
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f9fafb',
    position: 'relative'
  },
  errorBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    borderBottom: '1px solid #fecaca',
    color: '#dc2626',
    fontSize: '14px'
  },
  errorClose: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    color: '#dc2626',
    display: 'flex'
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    textAlign: 'center'
  },
  emptyTitle: {
    margin: '20px 0 8px 0',
    fontSize: '18px',
    fontWeight: '600',
    color: '#374151'
  },
  emptyText: {
    margin: 0,
    fontSize: '14px',
    color: '#9ca3af'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    width: '480px',
    maxWidth: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px'
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827'
  },
  modalClose: {
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    color: '#9ca3af',
    display: 'flex',
    borderRadius: '4px'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '8px'
  },
  modalSearchInput: {
    width: '100%',
    padding: '12px 12px 12px 44px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none'
  },
  loadingState: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    color: '#6b7280',
    fontSize: '13px'
  },
  patientList: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    maxHeight: '200px',
    overflow: 'auto',
    marginBottom: '20px'
  },
  patientItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    borderBottom: '1px solid #f3f4f6',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease'
  },
  patientAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '600',
    flexShrink: 0
  },
  patientInfo: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1
  },
  patientPhone: {
    color: '#6b7280',
    fontSize: '13px',
    marginTop: '2px'
  },
  selectedPatient: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#eff6ff',
    borderRadius: '8px',
    marginBottom: '20px'
  },
  changeButton: {
    background: 'none',
    border: 'none',
    color: '#3b82f6',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  textarea: {
    width: '100%',
    padding: '12px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    resize: 'vertical',
    fontFamily: 'inherit',
    outline: 'none'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: '1px solid #f3f4f6'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#fff',
    color: '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  sendButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  sendButtonDisabled: {
    backgroundColor: '#e5e7eb',
    color: '#9ca3af',
    cursor: 'not-allowed'
  }
};

const msgAiStyles = {
  summaryBar: {
    padding: '8px 16px',
    borderBottom: '1px solid #e5e7eb',
    background: '#fafafe'
  },
  summaryToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#667eea',
    background: '#f0f0ff',
    border: '1px solid #c7d2fe',
    borderRadius: '6px',
    cursor: 'pointer',
    width: '100%',
    justifyContent: 'center'
  },
  sparkle: {
    fontSize: '14px',
    color: '#f59e0b'
  },
  chevron: {
    fontSize: '10px',
    marginLeft: 'auto'
  },
  summaryPanel: {
    maxHeight: '300px',
    overflowY: 'auto',
    borderBottom: '1px solid #e5e7eb',
    background: '#fff'
  },
  insightsBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 16px',
    borderBottom: '1px solid #e5e7eb',
    background: '#fafbfc',
    flexWrap: 'wrap',
  },
  insightChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#374151',
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
  },
  insightSub: {
    color: '#9ca3af',
    fontSize: '11px',
    marginLeft: '2px',
  },
  draftPreview: {
    margin: '0 16px 8px',
    padding: '12px 14px',
    background: '#f0f0ff',
    border: '1px solid #c7d2fe',
    borderRadius: '8px',
  },
  draftHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
  },
  draftClose: {
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    fontSize: '11px',
    cursor: 'pointer',
  },
  draftText: {
    fontSize: '13px',
    color: '#374151',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
  },
  draftAccept: {
    padding: '5px 12px',
    background: '#667eea',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  draftReject: {
    padding: '5px 12px',
    background: '#fff',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '5px',
    fontSize: '12px',
    cursor: 'pointer',
  },
};

// Add CSS animation for spinner (inject once)
if (typeof document !== 'undefined' && !document.getElementById('messaging-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'messaging-styles';
  styleSheet.textContent = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleSheet);
}

export default Messaging;
